<script setup>
import { computed } from 'vue';
import {
  dateFormat,
  dynamicTime,
  shortTimestamp,
} from 'shared/helpers/timeHelper';
import useGlobalTimer from '../../composables/useGlobalTimer';

const props = defineProps({
  assignedAt: {
    type: Number,
    default: 0,
  },
  waitingSince: {
    type: Number,
    default: 0,
  },
  timerConfig: {
    type: Object,
    default: null,
  },
});

const MINUTE_IN_MILLI_SECONDS = 60000;

const { currentTime } = useGlobalTimer();

const elapsedTime = computed(() => {
  if (!props.assignedAt || !props.timerConfig) return null;

  // Force reactivity by accessing currentTime
  // eslint-disable-next-line no-unused-expressions
  currentTime.value;

  const timeString = dynamicTime(props.assignedAt);
  return shortTimestamp(timeString);
});

const tooltipText = computed(() => {
  if (!props.assignedAt) return '';
  return dateFormat(props.assignedAt);
});

const backgroundColorClass = computed(() => {
  if (!props.waitingSince || !props.timerConfig) {
    return 'bg-n-slate-4 dark:bg-n-slate-8 text-n-slate-12';
  }

  const { warning_threshold_minutes, critical_threshold_minutes } =
    props.timerConfig;

  const waitingSinceMs = props.waitingSince * 1000;
  const diffMs = currentTime.value - waitingSinceMs;
  const minutes = Math.floor(diffMs / MINUTE_IN_MILLI_SECONDS);

  if (minutes <= warning_threshold_minutes) {
    return 'bg-n-teal-3 text-n-teal-12';
  }
  if (minutes <= critical_threshold_minutes) {
    return 'bg-yellow-300 text-yellow-900';
  }
  return 'bg-n-ruby-4 text-n-ruby-10';
});

const shouldShowTimer = computed(() => {
  return props.assignedAt && elapsedTime.value && props.timerConfig;
});
</script>

<template>
  <div
    v-if="shouldShowTimer"
    v-tooltip.top="{
      content: tooltipText,
      delay: { show: 1000, hide: 0 },
      hideOnClick: true,
    }"
    class="ml-auto leading-5 text-xs rounded px-2 py-1 font-medium"
    :class="backgroundColorClass"
  >
    {{ elapsedTime }}
  </div>
</template>
